import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../utils/api';
import './ProjectsManagementPage.css';

const ProjectsManagementPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const [analyzePdfOnUpload, setAnalyzePdfOnUpload] = useState(true); // Controls whether to re-analyze PDF
  const [newImageUrl, setNewImageUrl] = useState('');
  const fileInputRef = useRef(null);

  // Fetch all projects
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get('/projects/');
      setProjects(data.projects || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Filter projects based on search term
  const filteredProjects = projects.filter(project => 
    project.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.location_description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Open project for editing
  const openEditModal = (project) => {
    setSelectedProject(project);
    setEditData({
      project_name: project.project_name || '',
      location_description: project.location_description || '',
      longitude: project.longitude || '',
      latitude: project.latitude || '',
      source_file: project.source_file || '',
      summary: project.summary || '',
      facilities: project.facilities || '',
      average_area: project.average_area || '',
      payment_plans: project.payment_plans || '',
      payment_plans_metadata: project.payment_plans_metadata || '',
      text: project.text || '',
      images: project.images || [],
      brochures: project.brochures || [],
    });
    setEditMode(true);
    setActiveTab('basic');
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/projects/${selectedProject.id}`, editData);

      alert('Project saved successfully!');
      await fetchProjects();
      setSelectedProject(null);
      setEditMode(false);
    } catch (err) {
      console.error('Error saving project:', err);
      alert('Failed to save project: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Handle delete project
  const handleDeleteProject = async (projectId, projectName, e) => {
    e.stopPropagation();
    
    if (!window.confirm(`Are you sure you want to delete "${projectName}"?\n\nThis will permanently remove the project from both the projects and data_projects tables.`)) {
      return;
    }

    try {
      await apiClient.delete(`/projects/${projectId}`);

      alert('Project deleted successfully!');
      await fetchProjects();
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Handle add image URL
  const handleAddImage = (url) => {
    if (!url.trim()) return;
    const newImages = [...(editData.images || []), url.trim()];
    setEditData(prev => ({ ...prev, images: newImages }));
  };

  // Handle remove image
  const handleRemoveImage = (index) => {
    const newImages = editData.images.filter((_, i) => i !== index);
    setEditData(prev => ({ ...prev, images: newImages }));
  };

  // Handle brochure upload
  const handleBrochureUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingBrochure(true);
    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      // Add analyze_pdf query parameter based on checkbox state
      const { data: result } = await apiClient.post(
        `/projects/${selectedProject.id}/brochures?analyze_pdf=${analyzePdfOnUpload}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // Update local edit data with new brochure
      const newBrochures = [...(editData.brochures || []), result.brochure_url];
      setEditData(prev => ({ ...prev, brochures: newBrochures }));
      
      // Show appropriate success message based on whether analysis was performed
      if (analyzePdfOnUpload && result.extracted_fields && result.extracted_fields.length > 0) {
        alert(`Brochure uploaded and analyzed! Updated fields: ${result.extracted_fields.join(', ')}`);
        // Refresh project data since fields were updated
        const { data: projectsData } = await apiClient.get('/projects/');
        const updatedProjects = projectsData.projects || [];
        setProjects(updatedProjects);
        
        // Find the updated project from the freshly fetched list
        const freshProject = updatedProjects.find(p => p.id === selectedProject.id);
        if (freshProject) {
          // Update selected project
          setSelectedProject(freshProject);
          // Update edit data matching openEditModal structure
          setEditData({
            project_name: freshProject.project_name || '',
            location_description: freshProject.location_description || '',
            longitude: freshProject.longitude || '',
            latitude: freshProject.latitude || '',
            source_file: freshProject.source_file || '',
            summary: freshProject.summary || '',
            facilities: freshProject.facilities || '',
            average_area: freshProject.average_area || '',
            payment_plans: freshProject.payment_plans || '',
            payment_plans_metadata: freshProject.payment_plans_metadata || '',
            text: freshProject.text || '',
            images: freshProject.images || [],
            brochures: freshProject.brochures || [],
          });
        }
      } else {
        alert('Brochure uploaded successfully!');
      }
    } catch (err) {
      console.error('Error uploading brochure:', err);
      alert('Failed to upload brochure: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingBrochure(false);
    }
  };

  // Handle remove brochure
  const handleRemoveBrochure = async (brochureUrl, index) => {
    if (!window.confirm('Are you sure you want to remove this brochure?')) return;

    try {
      await apiClient.delete(
        `/projects/${selectedProject.id}/brochures?brochure_url=${encodeURIComponent(brochureUrl)}`
      );
      
      const newBrochures = editData.brochures.filter((_, i) => i !== index);
      setEditData(prev => ({ ...prev, brochures: newBrochures }));
    } catch (err) {
      console.error('Error deleting brochure:', err);
      alert('Failed to delete brochure: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Project Card Component
  const ProjectCard = ({ project }) => {
    const hasBrochures = project.brochures && project.brochures.length > 0;
    const hasImages = project.images && project.images.length > 0;
    const hasUnits = project.has_units || project.property_unit_count > 0;
    const imageCount = project.images?.length || 0;
    const brochureCount = project.brochures?.length || 0;
    const unitCount = project.property_unit_count || 0;

    return (
      <div className="project-card" onClick={() => openEditModal(project)}>
        {/* Card Image/Header Area */}
        <div className="project-card-image">
          {hasImages ? (
            <img src={project.images[0]} alt={project.project_name} />
          ) : (
            <div className="project-card-placeholder">
              <span className="placeholder-icon">🏢</span>
            </div>
          )}
          <div className="project-card-overlay">
            <div className="project-badges">
              {hasImages && (
                <span className="badge badge-image">
                  <span className="badge-icon">📷</span> {imageCount}
                </span>
              )}
              {hasBrochures && (
                <span className="badge badge-brochure">
                  <span className="badge-icon">📄</span> {brochureCount}
                </span>
              )}
              {hasUnits && (
                <span className="badge badge-excel">
                  <span className="badge-icon">📊</span> {unitCount}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="project-card-content">
          <h3 className="project-name">{project.project_name}</h3>
          
          <div className="project-location">
            <span className="location-pin">📍</span>
            <span className="location-text">{project.location_description || 'Location not specified'}</span>
          </div>
          
          {project.summary && (
            <p className="project-summary">
              {project.summary.length > 120 
                ? project.summary.substring(0, 120) + '...' 
                : project.summary}
            </p>
          )}
        </div>

        <div className="project-card-footer">
          <div className="footer-left">
            <span className="source-tag">
              {project.source_file ? project.source_file.split('/').pop() : 'No source'}
            </span>
          </div>
          <div className="footer-right">
            <button 
              className="units-btn"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/property/${project.id}?name=${encodeURIComponent(project.project_name)}`, '_blank');
              }}
              title="Manage property units"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
            </button>
            <button 
              className="delete-btn"
              onClick={(e) => handleDeleteProject(project.id, project.project_name, e)}
              title="Delete project"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Close modal helper
  const closeEditModal = () => {
    setSelectedProject(null);
    setEditMode(false);
    setNewImageUrl('');
  };

  return (
    <div className="projects-management-page">
      <div className="page-header">
        <h1>🏢 Projects Management</h1>
        <p className="page-subtitle">View, edit, and manage project data</p>
      </div>

      {/* Search and Stats Bar */}
      <div className="toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search projects by name or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>
          )}
        </div>
        
        <div className="stats">
          <span className="stat-item">
            <strong>{projects.length}</strong> Total Projects
          </span>
          <span className="stat-item">
            <strong>{projects.filter(p => p.brochures?.length > 0).length}</strong> With Brochures
          </span>
          <span className="stat-item">
            <strong>{projects.filter(p => p.images?.length > 0).length}</strong> With Images
          </span>
        </div>

        <button className="btn btn-refresh" onClick={fetchProjects} disabled={loading}>
          🔄 Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading projects...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button className="btn btn-retry" onClick={fetchProjects}>Try Again</button>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📭</span>
          <p>{searchTerm ? 'No projects match your search' : 'No projects found'}</p>
        </div>
      ) : (
        <div className="projects-grid">
          {filteredProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}

      {/* Edit Modal - Rendered inline to prevent focus loss */}
      {selectedProject && editMode && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content edit-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeEditModal}>×</button>
            
            <div className="modal-header">
              <h2>Edit Project</h2>
              <p className="modal-subtitle">{editData.project_name}</p>
            </div>

            {/* Tabs */}
            <div className="modal-tabs">
              <button 
                className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                onClick={() => setActiveTab('basic')}
              >
                📋 Basic Info
              </button>
              <button 
                className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                📝 Details
              </button>
              <button 
                className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
                onClick={() => setActiveTab('content')}
              >
                📄 Content
              </button>
              <button 
                className={`tab-btn ${activeTab === 'media' ? 'active' : ''}`}
                onClick={() => setActiveTab('media')}
              >
                🖼️ Media
              </button>
            </div>

            <div className="modal-body">
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <div className="tab-content">
                  <div className="form-group">
                    <label>Project Name</label>
                    <input 
                      type="text"
                      value={editData.project_name || ''}
                      onChange={(e) => handleInputChange('project_name', e.target.value)}
                      placeholder="Enter project name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Location Description</label>
                    <textarea 
                      value={editData.location_description || ''}
                      onChange={(e) => handleInputChange('location_description', e.target.value)}
                      placeholder="Enter location description"
                      rows={3}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Longitude</label>
                      <input 
                        type="number"
                        step="any"
                        value={editData.longitude || ''}
                        onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value) || '')}
                        placeholder="e.g., 31.2357"
                      />
                    </div>
                    <div className="form-group">
                      <label>Latitude</label>
                      <input 
                        type="number"
                        step="any"
                        value={editData.latitude || ''}
                        onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value) || '')}
                        placeholder="e.g., 30.0444"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Source File</label>
                    <input 
                      type="text"
                      value={editData.source_file || ''}
                      onChange={(e) => handleInputChange('source_file', e.target.value)}
                      placeholder="Enter source file name"
                    />
                  </div>
                </div>
              )}

              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="tab-content">
                  <div className="form-group">
                    <label>Summary</label>
                    <textarea 
                      value={editData.summary || ''}
                      onChange={(e) => handleInputChange('summary', e.target.value)}
                      placeholder="Enter project summary"
                      rows={4}
                    />
                  </div>

                  <div className="form-group">
                    <label>Facilities</label>
                    <textarea 
                      value={editData.facilities || ''}
                      onChange={(e) => handleInputChange('facilities', e.target.value)}
                      placeholder="Enter facilities information"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Average Area</label>
                    <input 
                      type="text"
                      value={editData.average_area || ''}
                      onChange={(e) => handleInputChange('average_area', e.target.value)}
                      placeholder="e.g., 120-250 sqm"
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Plans</label>
                    <textarea 
                      value={editData.payment_plans_metadata || editData.payment_plans || ''}
                      onChange={(e) => handleInputChange('payment_plans_metadata', e.target.value)}
                      placeholder="Enter payment plan details"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* Content Tab */}
              {activeTab === 'content' && (
                <div className="tab-content">
                  <div className="form-group read-only-group">
                    <label>
                      Raw Text Content
                      <span className="label-badge read-only-badge">🔒 Read Only</span>
                    </label>
                    <div className="read-only-notice">
                      <span className="notice-icon">ℹ️</span>
                      <span>This field is auto-generated from other project fields and cannot be edited directly.</span>
                    </div>
                    <textarea 
                      className="raw-text-field read-only-field"
                      value={editData.text || ''}
                      readOnly
                      placeholder="No content generated yet..."
                      rows={15}
                    />
                    <p className="field-hint">
                      This searchable text is automatically regenerated when you save the project, 
                      combining data from Basic Info, Details, and other fields.
                    </p>
                  </div>
                </div>
              )}

              {/* Media Tab */}
              {activeTab === 'media' && (
                <div className="tab-content">
                  {/* Images Section */}
                  <div className="media-section">
                    <h3>🖼️ Images</h3>
                    
                    {editData.images && editData.images.length > 0 ? (
                      <div className="media-list images-list">
                        {editData.images.map((url, index) => (
                          <div key={index} className="media-item image-item">
                            <img src={url} alt={`Project ${index + 1}`} className="image-thumbnail" />
                            <span className="media-url" title={url}>{url.length > 40 ? url.substring(0, 40) + '...' : url}</span>
                            <button 
                              className="remove-media-btn"
                              onClick={() => handleRemoveImage(index)}
                              title="Remove image"
                            >
                              ❌
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-media">No images added yet</p>
                    )}

                    <div className="add-media-form">
                      <input 
                        type="text"
                        placeholder="Enter image URL..."
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddImage(newImageUrl);
                            setNewImageUrl('');
                          }
                        }}
                      />
                      <button 
                        className="btn btn-add"
                        onClick={() => {
                          handleAddImage(newImageUrl);
                          setNewImageUrl('');
                        }}
                      >
                        + Add Image
                      </button>
                    </div>
                  </div>

                  {/* Brochures Section */}
                  <div className="media-section">
                    <h3>📄 Brochures</h3>
                    
                    {editData.brochures && editData.brochures.length > 0 ? (
                      <div className="media-list brochures-list">
                        {editData.brochures.map((url, index) => (
                          <div key={index} className="media-item brochure-item">
                            <span className="brochure-icon">📄</span>
                            <a href={url} target="_blank" rel="noopener noreferrer" className="brochure-link">
                              Brochure {index + 1}
                            </a>
                            <button 
                              className="remove-media-btn"
                              onClick={() => handleRemoveBrochure(url, index)}
                              title="Remove brochure"
                            >
                              ❌
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-media">No brochures uploaded yet</p>
                    )}

                    <div className="upload-area">
                      <input 
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        onChange={(e) => handleBrochureUpload(e.target.files)}
                        style={{ display: 'none' }}
                      />
                      <div className="upload-options">
                        <label className="analyze-checkbox">
                          <input
                            type="checkbox"
                            checked={analyzePdfOnUpload}
                            onChange={(e) => setAnalyzePdfOnUpload(e.target.checked)}
                          />
                          <span className="checkbox-label">
                            🤖 Re-analyze PDF to update project data (summary, location, facilities, etc.)
                          </span>
                        </label>
                      </div>
                      <button 
                        className="btn btn-upload-brochure"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingBrochure}
                      >
                        {uploadingBrochure 
                          ? (analyzePdfOnUpload ? '⏳ Analyzing & Uploading...' : '⏳ Uploading...') 
                          : '📤 Upload PDF Brochure'}
                      </button>
                      {!analyzePdfOnUpload && (
                        <p className="upload-hint">PDF will be uploaded without re-analyzing project data</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button 
                className="btn btn-cancel"
                onClick={closeEditModal}
              >
                Cancel
              </button>
              <button 
                className="btn btn-save"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '⏳ Saving...' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsManagementPage;
